/**
 * Budgets: the writes. A budget is its own row, not a kind of plan — it holds
 * some categories and tags to a figure over a period rather than expecting a
 * transaction on a day (see `lib/budgets.js` for what that means).
 *
 * What a budget covers lives in two link tables, `budget_categories` (by
 * category id) and `budget_tags`. A loaded budget carries them as
 * `categoryIds` and `tagIds`; the names are resolved where they are shown, so
 * a rename never leaves a budget saying the old one.
 */
import { insert, query, remove, run, update } from '../db'
import { uid } from '../lib/id'
import { round2 } from '../lib/money'
import { today as todayISO } from '../lib/dates'
import { autoPeriodsOf } from '../lib/budgets'
import { state } from './state'
import { addCategory } from './categories'

export async function loadBudgets() {
  const [budgets, categories, tags] = await Promise.all([
    query('SELECT * FROM budgets ORDER BY created_at'),
    query('SELECT budget_id, category_id FROM budget_categories'),
    query('SELECT budget_id, tag_id FROM budget_tags'),
  ])
  const group = (links, key) => {
    const out = {}
    for (const link of links) (out[link.budget_id] ??= []).push(link[key])
    return out
  }
  const categoryIds = group(categories, 'category_id')
  const tagIds = group(tags, 'tag_id')
  state.budgets = budgets.map((budget) => ({
    ...budget,
    categoryIds: categoryIds[budget.id] ?? [],
    tagIds: tagIds[budget.id] ?? [],
  }))
  return state.budgets
}

function normalise(input) {
  return {
    type: input.type === 'income' ? 'income' : 'expense',
    amount: Math.abs(round2(Number(input.amount) || 0)),
    cadence: input.cadence ?? 'monthly',
    tolerance: Number(input.tolerance ?? 0.1),
    start_date: input.start_date || todayISO(),
    end_date: input.end_date || null,
    archived: input.archived ? 1 : 0,
    // The figure is worked out from the last N periods instead; `amount` is
    // kept, so switching auto off gives back the figure that was there.
    auto: input.auto ? 1 : 0,
    auto_periods: autoPeriodsOf(input),
  }
}

/**
 * The category ids for these names, making any that do not exist yet — typing
 * a name is how a category comes into being, here as everywhere else.
 */
async function categoryIdsFor(names) {
  const ids = []
  for (const name of names ?? []) {
    const category = await addCategory(name)
    if (category && !ids.includes(category.id)) ids.push(category.id)
  }
  return ids
}

/** Replaces what the budget covers with exactly these, in one transaction. */
async function writeLinks(budgetId, categoryIds, tagIds) {
  await run('DELETE FROM budget_categories WHERE budget_id = ?', [budgetId])
  await run('DELETE FROM budget_tags WHERE budget_id = ?', [budgetId])
  for (const id of new Set(categoryIds)) {
    await run('INSERT INTO budget_categories (budget_id, category_id) VALUES (?, ?)', [budgetId, id])
  }
  for (const id of new Set(tagIds)) {
    await run('INSERT INTO budget_tags (budget_id, tag_id) VALUES (?, ?)', [budgetId, id])
  }
}

async function inTransaction(work) {
  await run('BEGIN')
  try {
    await work()
    await run('COMMIT')
  } catch (err) {
    await run('ROLLBACK')
    throw err
  }
}

/**
 * @param {object} input  the budget's fields, plus `categories` (names) and
 *                        `tags` (tag ids) for what it covers
 */
export async function addBudget(input) {
  const now = new Date().toISOString()
  const row = { id: uid('bg'), ...normalise(input), created_at: now, updated_at: now }
  const categoryIds = await categoryIdsFor(input.categories)
  await inTransaction(async () => {
    await insert('budgets', row)
    await writeLinks(row.id, categoryIds, input.tags ?? [])
  })
  await loadBudgets()
  return row
}

export async function updateBudget(id, input) {
  const row = normalise(input)
  const categoryIds = await categoryIdsFor(input.categories)
  await inTransaction(async () => {
    await update('budgets', id, { ...row, updated_at: new Date().toISOString() })
    await writeLinks(id, categoryIds, input.tags ?? [])
  })
  await loadBudgets()
}

export async function deleteBudget(id) {
  // Nothing points at a budget but its own links, which go with it.
  await remove('budgets', id)
  await loadBudgets()
}

export async function toggleBudgetArchived(id, archived) {
  await update('budgets', id, { archived: archived ? 1 : 0, updated_at: new Date().toISOString() })
  await loadBudgets()
}
